import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, User } from 'lucide-react';
import { readCustomFieldValue } from '../../lib/chartDefinitions';
import { tierLabel, type ProfileScoreTier } from '../../lib/personalization';
import {
  SUBSCRIBER_EMAIL_STATUSES,
  subscriberEmailStatus,
} from '../../lib/subscriberEmailStatus';
import { subscriberFunnelStatusLabel } from '../../lib/subscriberFunnelStatus';
import { subscriberRoleLabel } from '../../lib/subscriberRoles';
import { subscriberSource, subscriberSourceLabel } from '../../lib/subscriberSource';
import { supabase } from '../../lib/supabase';
import type { SubscriberRow } from '../Subscribers/types';

const OPEN_DELAY_MS = 220;
/** Long enough to move from the email into the panel across the gap. */
const CLOSE_DELAY_MS = 450;
const PANEL_WIDTH = 320;
const GAP_PX = 10;

export type SubscriberHoverSeed = {
  email?: string | null;
  name?: string | null;
  role?: string | null;
  email_status?: string | null;
  funnel_status?: string | null;
  source?: string | null;
  profile_score?: number | null;
  profile_score_tier?: ProfileScoreTier | string | null;
  custom_data?: unknown;
};

type PanelProfile = {
  id: string | null;
  email: string;
  name: string;
  role: string;
  emailStatus: string;
  funnel: string;
  source: string;
  profileScore: number | null;
  profileTier: string;
  photoUrl: string | null;
};

function photoFromCustomData(customData: unknown): string | null {
  const url = readCustomFieldValue(customData, 'photo_url');
  return url.startsWith('http') ? url : null;
}

function emailStatusLabel(value: string): string {
  if (!value) return '';
  return SUBSCRIBER_EMAIL_STATUSES.find((s) => s.value === value)?.label ?? value;
}

function profileFromSeed(
  subscriberId: string | null | undefined,
  seed: SubscriberHoverSeed | undefined,
): PanelProfile | null {
  const email = seed?.email?.trim() ?? '';
  if (!email && !subscriberId) return null;
  const tier = seed?.profile_score_tier;
  return {
    id: subscriberId?.trim() || null,
    email,
    name: seed?.name?.trim() ?? '',
    role: subscriberRoleLabel(seed?.role) || (seed?.role?.trim() ?? ''),
    emailStatus: emailStatusLabel(
      subscriberEmailStatus(seed as SubscriberRow) || (seed?.email_status?.trim() ?? ''),
    ),
    funnel: subscriberFunnelStatusLabel(seed?.funnel_status),
    source: (() => {
      const src = subscriberSource(seed as SubscriberRow);
      return src ? subscriberSourceLabel(src) || src : '';
    })(),
    profileScore: typeof seed?.profile_score === 'number' ? seed.profile_score : null,
    profileTier:
      typeof tier === 'string' && tier
        ? tierLabel(tier as ProfileScoreTier) || tier
        : '',
    photoUrl: photoFromCustomData(seed?.custom_data),
  };
}

function profileFromRow(row: SubscriberRow): PanelProfile {
  const email =
    (typeof row.email === 'string' && row.email.trim()) ||
    (typeof row.email_address === 'string' && row.email_address.trim()) ||
    '';
  const name =
    (typeof row.name === 'string' && row.name.trim()) ||
    (typeof row.full_name === 'string' && row.full_name.trim()) ||
    '';
  const src = subscriberSource(row);
  const tier = row.profile_score_tier;
  return {
    id: row.id != null ? String(row.id) : null,
    email,
    name,
    role: subscriberRoleLabel(row.role) || (typeof row.role === 'string' ? row.role.trim() : ''),
    emailStatus: emailStatusLabel(subscriberEmailStatus(row)),
    funnel: subscriberFunnelStatusLabel(row.funnel_status),
    source: src ? subscriberSourceLabel(src) || src : '',
    profileScore: typeof row.profile_score === 'number' ? row.profile_score : null,
    profileTier:
      typeof tier === 'string' && tier ? tierLabel(tier as ProfileScoreTier) || tier : '',
    photoUrl: photoFromCustomData(row.custom_data),
  };
}

function MetaRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[5.5rem_1fr] gap-2 text-xs">
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-gray-900 dark:text-gray-100 min-w-0 break-words">{value}</dd>
    </div>
  );
}

export default function SubscriberHoverCard({
  subscriberId,
  seed,
  children,
  className = '',
}: {
  subscriberId?: string | null;
  seed?: SubscriberHoverSeed;
  children: ReactNode;
  className?: string;
}) {
  const panelId = useId();
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    bridge: { top: number; left: number; width: number; height: number };
    side: 'right' | 'left';
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<PanelProfile | null>(() =>
    profileFromSeed(subscriberId, seed),
  );

  const clearTimers = useCallback(() => {
    if (openTimer.current != null) window.clearTimeout(openTimer.current);
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  const updatePosition = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelEl = panelRef.current;
    const panelHeight = panelEl?.offsetHeight || 280;
    const margin = 12;

    const spaceRight = window.innerWidth - rect.right - margin;
    const side: 'right' | 'left' =
      spaceRight >= PANEL_WIDTH + GAP_PX ? 'right' : 'left';

    let left =
      side === 'right'
        ? rect.right + GAP_PX
        : rect.left - GAP_PX - PANEL_WIDTH;
    left = Math.min(Math.max(margin, left), window.innerWidth - PANEL_WIDTH - margin);

    let top = rect.top;
    if (top + panelHeight > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - panelHeight - margin);
    }
    if (top < margin) top = margin;

    // Invisible hover bridge covering the gap so the pointer can travel into the panel.
    const bridgeLeft = side === 'right' ? rect.right : left + PANEL_WIDTH;
    const bridgeWidth = Math.max(GAP_PX, Math.abs((side === 'right' ? left : rect.left) - rect.right));
    const bridgeTop = Math.min(rect.top, top);
    const bridgeHeight = Math.max(rect.bottom, top + panelHeight) - bridgeTop;

    setCoords({
      top,
      left,
      side,
      bridge: {
        top: bridgeTop,
        left: bridgeLeft,
        width: bridgeWidth + 2,
        height: Math.max(bridgeHeight, rect.height),
      },
    });
  }, []);

  const loadProfile = useCallback(async () => {
    const id = subscriberId?.trim();
    const email = seed?.email?.trim();
    if (!id && !email) return;

    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('subscribers')
        .select(
          'id, email, name, role, funnel_status, email_status, source, profile_score, profile_score_tier, custom_data',
        )
        .limit(1);
      query = id ? query.eq('id', id) : query.ilike('email', email!);
      const { data, error: qError } = await query.maybeSingle();
      if (qError) throw qError;
      if (data) {
        setProfile(profileFromRow(data as SubscriberRow));
      } else if (!profileFromSeed(subscriberId, seed)) {
        setError('No connection found');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load profile');
    } finally {
      setLoading(false);
    }
  }, [seed, subscriberId]);

  const scheduleOpen = () => {
    clearTimers();
    openTimer.current = window.setTimeout(() => {
      setProfile(profileFromSeed(subscriberId, seed));
      setOpen(true);
      void loadProfile();
    }, OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    clearTimers();
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearTimers(), [clearTimers]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    // Re-measure after paint so we use the real panel height.
    const raf = window.requestAnimationFrame(() => updatePosition());
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updatePosition, profile, loading]);

  const seedKey = [
    subscriberId ?? '',
    seed?.email ?? '',
    seed?.name ?? '',
    seed?.role ?? '',
    seed?.email_status ?? '',
    seed?.funnel_status ?? '',
    seed?.source ?? '',
    seed?.profile_score ?? '',
    seed?.profile_score_tier ?? '',
  ].join('|');

  useEffect(() => {
    setProfile(profileFromSeed(subscriberId, seed));
    // seedKey captures the fields we care about without depending on object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional seedKey sync
  }, [seedKey, subscriberId]);

  const display = profile ?? profileFromSeed(subscriberId, seed);
  const href = display?.id ? `/subscribers/${display.id}` : null;

  const keepOpen = () => {
    cancelClose();
    setOpen(true);
  };

  return (
    <span
      ref={rootRef}
      className={`relative inline-flex max-w-full min-w-0 ${className}`.trim()}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
    >
      {children}
      {open && coords
        ? createPortal(
            <>
              {/* Hit area across the gap so the panel doesn't close while moving into it. */}
              <div
                aria-hidden
                className="fixed z-[89]"
                style={{
                  top: coords.bridge.top,
                  left: coords.bridge.left,
                  width: coords.bridge.width,
                  height: coords.bridge.height,
                }}
                onMouseEnter={keepOpen}
                onMouseLeave={scheduleClose}
              />
              <div
                ref={panelRef}
                id={panelId}
                role="dialog"
                aria-label="Connection preview"
                className="fixed z-[90] w-[min(20rem,calc(100vw-1.5rem))] rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl p-3.5"
                style={{ top: coords.top, left: coords.left }}
                onMouseEnter={keepOpen}
                onMouseLeave={scheduleClose}
              >
                {loading && !display ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
                  </div>
                ) : error && !display ? (
                  <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
                ) : display ? (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 min-w-0">
                      {display.photoUrl ? (
                        <img
                          src={display.photoUrl}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover shrink-0 border border-gray-200 dark:border-gray-600"
                        />
                      ) : (
                        <span className="w-12 h-12 rounded-full shrink-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                          <User className="w-5 h-5" />
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {display.name || 'No name on file'}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-300 break-all">
                          {display.email || '—'}
                        </p>
                        {loading ? (
                          <p className="mt-1 text-[11px] text-gray-400 inline-flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Refreshing…
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <dl className="space-y-1.5">
                      <MetaRow label="Role" value={display.role} />
                      <MetaRow label="Email status" value={display.emailStatus} />
                      <MetaRow label="Funnel" value={display.funnel} />
                      <MetaRow label="Source" value={display.source} />
                      <MetaRow
                        label="Profile"
                        value={
                          display.profileScore != null
                            ? `${display.profileScore}${display.profileTier ? ` · ${display.profileTier}` : ''}`
                            : ''
                        }
                      />
                    </dl>

                    {!display.role &&
                    !display.emailStatus &&
                    !display.funnel &&
                    !display.source &&
                    display.profileScore == null &&
                    !display.photoUrl ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                        Limited profile on file — open the connection for full details.
                      </p>
                    ) : null}

                    {href ? (
                      <Link
                        to={href}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Open connection
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No connection preview.</p>
                )}
              </div>
            </>,
            document.body,
          )
        : null}
    </span>
  );
}
